import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, map, catchError, filter } from 'rxjs/operators';

import { SendOrderStatusAnalyticCustomDataEvent, SendOrderStatusAnalyticCustomPageEvent } from './order-status-analytics.actions';
import {
    SetOrderError,
    SetOrderErrorTracking,
    SetOrderGuest,
    SetOrderGuestError,
    SetOrderGuestSuccess,
    SetOrderNumber,
    SetOrderStatus,
    SetOrderSuccess,
    SetStepOrderStatus,
} from './order-status.actions';

import { OrderStatusStep } from '@api/order/order.models';
import { OrderService } from '@api/order/order.service';
import { getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { GA4EventType } from '@shared/analytics/GA4/models/general.model';
import { GA4AnalyticsPublicActions } from '@shared/analytics/GA4/public.api';
import { selectUserIsLogged } from '@shared/user/store';

@Injectable()
export class OrderStatusEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly orderService: OrderService,
        private readonly store: Store,
    ) { }

    public loadOrderNoUser$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetOrderNumber),
            concatLatestFrom(() => [
                this.store.select(selectUserIsLogged),
                this.store.select(getStoreAndLanguage),
            ]),
            filter(([_, isLogged]) => !isLogged),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap(([action, _, { languageId, storeId }]) => {
                return this.orderService.tracking(
                    storeId,
                    languageId,
                    action.orderNumber,
                ).pipe(
                    map((order) => SetOrderStatus({ orderStatus: order })),
                    catchError((error: Error) => {
                        return of(SetOrderErrorTracking({ error: error.message, orderId: action.orderNumber }));
                    })
                );
            })
        );
    });

    public loadOrder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetOrderNumber),
            concatLatestFrom(() => [
                this.store.select(selectUserIsLogged),
            ]),

            filter(([_, isLogged]) => isLogged),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap(([action]) => {
                return this.orderService.getOrderById(
                    +action.orderNumber,
                    true,
                ).pipe(
                    map((order) => SetOrderSuccess({ orderStatus: order })),
                    catchError((error: Error) => {
                        return of(SetOrderError({ error: error.message, orderId: action.orderNumber }));
                    })
                );
            })
        );
    });

    public loadOrderOnError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetOrderError),
            concatLatestFrom(() => [
                this.store.select(getStoreAndLanguage),
            ]),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap(([action, { languageId, storeId }]) => {
                return this.orderService.tracking(
                    storeId,
                    languageId,
                    action.orderId,
                ).pipe(
                    map((order) => SetOrderStatus({ orderStatus: order })),
                    catchError((error: Error) => {
                        return of(SetOrderErrorTracking({ error: error.message, orderId: action.orderId }));
                    })
                );
            })
        );
    });

    public onOrderErrorTrackingSetStep$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetOrderErrorTracking, SetOrderGuestError),
            map((_) => SetStepOrderStatus({ step: OrderStatusStep.NOT_FOUND })),
        );
    });

    public onOrderErrorTrackingSetAnaliticEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetOrderErrorTracking, SetOrderGuestError),
            map((action) => SendOrderStatusAnalyticCustomDataEvent({ actionType: 'cero_resultados', orderId: action.orderId })),
        );
    });

    public loadOrderGuest$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetOrderGuest),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap((action) => {
                return this.orderService.getOrderByIdGuest(
                    action.orderId,
                    action.guestId,
                ).pipe(
                    map((order) => SetOrderGuestSuccess({ orderStatus: order })),
                    catchError((error: Error) => {
                        return of(SetOrderGuestError({ error: error.message, orderId: action.orderId.toString() }));
                    })
                );
            })
        );
    });

    public onOrderGuestSuccessNullError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetOrderGuestSuccess, SetOrderStatus, SetOrderSuccess),
            filter((action) => action.orderStatus === null || action.orderStatus?.emptyResponse),
            map((_) => {
                return SetStepOrderStatus({ step: OrderStatusStep.NOT_FOUND });
            }),
        );
    });

    public onOrderGuestSuccessSetStep$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetOrderGuestSuccess, SetOrderStatus, SetOrderSuccess),
            filter((action) => action.orderStatus !== null && !action.orderStatus?.emptyResponse),
            map((_) => {
                return SetStepOrderStatus({ step: OrderStatusStep.FOUND });
            }),
        );
    });

    public onOrderGuestSuccessSetPage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetOrderGuestSuccess, SetOrderStatus, SetOrderSuccess),
            filter((action) => action.orderStatus !== null && !action.orderStatus?.emptyResponse),
            map((_) => GA4AnalyticsPublicActions.Ga4Event({
                name: GA4EventType.page_view,
                eventFrom: EventFrom.orderStatusList,
            })),
        );
    });

    public onOrderGuestSuccessAnaliticCustomEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetOrderGuestSuccess, SetOrderStatus, SetOrderSuccess),
            filter((action) => action.orderStatus !== null && !action.orderStatus?.emptyResponse),
            map((action) => SendOrderStatusAnalyticCustomDataEvent({ actionType: 'ver_estado_pedido', orderId: `${action.orderStatus.id}` })),
        );
    });

    public onOrderGuestSuccessAnaliticEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetOrderGuestSuccess, SetOrderStatus, SetOrderSuccess),
            filter((action) => !action.orderStatus?.emptyResponse),
            map((action) => SendOrderStatusAnalyticCustomPageEvent({ actionType: 'lista', orderId: `${action.orderStatus.id}`, orderState: action.orderStatus.statusText })),
        );
    });
}
